'use client';

import { PromotionStar } from '@ahafood/icons';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { JSFunction } from '@ahm/common-helpers';
import { MapPin, Facebook } from 'lucide-react';
import Image from 'next/image';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';

interface Store {
  _id: number;
  name: string;
  image_url: string;
  address: string;
  distance: number;
  page_id: number;
  ai_tags?: string[];
  nearest_branch?: {
    address: string;
  };
}

interface StoreHeaderProps {
  store: Store;
  onOpenFacebook: (pageId: number) => void;
  onSendMessage: (pageId: number) => void;
  isMobileView?: boolean;
}

export function StoreHeader({
  store,
  onOpenFacebook,
  onSendMessage,
  isMobileView = false,
}: StoreHeaderProps) {
  const isMobile = useIsMobile();
  const isWebinApp = useDetectWebInApp();

  const isValidPageId = useCallback((pageId: number) => {
    return pageId && pageId > 0;
  }, []);

  const openFacebookOnDesktop = useCallback((pageId: number) => {
    window.open(`https://www.facebook.com/${pageId}`, '_blank');
  }, []);

  const handleOpenFacebook = useCallback(
    (pageId: number) => {
      if (!isValidPageId(pageId)) {
        toast.error('Không thể mở fanpage của cửa hàng này, vui lòng thử lại.');
        return;
      }

      if (isMobile && isWebinApp) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        JSFunction.call({
          name: 'openPage',
          body: {
            url: deeplink,
            fallback,
            type: 'external_app',
          },
        });
      } else if (isMobile) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        const tempLink = document.createElement('a');
        tempLink.href = deeplink;
        tempLink.click();

        setTimeout(() => {
          window.open(fallback, '_blank');
        }, 1000);
      } else {
        openFacebookOnDesktop(pageId);
      }
    },
    [isMobile, isValidPageId, isWebinApp, openFacebookOnDesktop]
  );

  if (isMobileView) {
    return (
      <div className="p-4 bg-white">
        <h1 className="text-md font-bold text-[#1a1a1a] mb-2">
          {store?.name ?? 'Store Name'}
        </h1>

        <div className="flex items-start content-center text-sm font-medium mb-3 w-full">
          <MapPin className="size-4 mr-1 text-brand-primary shrink-0 mt-0.5" />
          <span className="text-neutral-50 ml-2 leading-relaxed block">
            {store?.distance > 0 && (
              <span className="inline-block flex-none text-brand-primary mr-2 font-medium">
                {(store.distance / 1000).toFixed(1)} km
              </span>
            )}
            <span>{store?.nearest_branch?.address ?? store?.address}</span>
          </span>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {store?.ai_tags?.map((tag, index) => (
            <Badge
              key={index}
              className="flex-nowrap border-yellow-30 px-1.5 py-0 bg-white text-sm font-semibold capitalize text-yellow-30"
            >
              <PromotionStar className="mr-1 size-4" />
              {tag}
            </Badge>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-row gap-2 mb-4 w-full">
          <Button
            className="flex-1 border border-blue-50 text-blue-50 hover:bg-blue-50 hover:text-white font-bold rounded-sm"
            onClick={() => handleOpenFacebook(store?.page_id ?? 0)}
            variant="outline"
          >
            <Facebook className="size-4" />
            Xem fanpage
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-yellow-40 to-primary-50 text-white font-bold rounded-sm"
            onClick={() => onSendMessage(store?.page_id ?? 0)}
            variant="default"
          >
            Chat với cửa hàng
          </Button>
        </div>
      </div>
    );
  }

  // Desktop view
  return (
    <div className="mb-8">
      <div className="flex flex-row gap-6 mb-8">
        {/* Hero Image */}
        <div className="relative h-64 xl:h-80 overflow-hidden rounded-md flex-1">
          <Image
            src={
              store?.image_url && store.image_url.trim() !== ''
                ? store.image_url
                : `${assetPrefix}/static/images/empty-store.webp`
            }
            alt={store?.name ?? 'Store image'}
            fill
            className="object-cover"
            priority
          />
        </div>

        {/* Restaurant Info */}
        <div className="flex-1">
          <h1 className="text-2xl xl:text-3xl font-bold text-[#1a1a1a] mb-4">
            {store?.name ?? 'Store Name'}
          </h1>

          <div className="flex items-start content-center text-base font-medium mb-6 w-full">
            <MapPin className="size-5 mr-1 text-brand-primary shrink-0 mt-0.5" />
            <span className="text-neutral-50 ml-2 leading-relaxed block">
              {store?.distance > 0 && (
                <span className="inline-block flex-none text-brand-primary mr-2 font-medium">
                  {(store.distance / 1000).toFixed(1)} km
                </span>
              )}
              <span>{store?.address}</span>
            </span>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            {store?.ai_tags?.map((tag, index) => (
              <Badge
                key={index}
                className="flex-nowrap border-yellow-30 px-3 py-1 bg-white text-base font-semibold capitalize text-yellow-30"
              >
                <PromotionStar className="mr-1 size-5" />
                {tag}
              </Badge>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row gap-4 mb-6 w-full">
            <Button
              className="flex-1 border border-blue-50 text-blue-50 hover:bg-blue-50 hover:text-white font-bold rounded-sm py-3 text-base"
              onClick={() => handleOpenFacebook(store?.page_id ?? 0)}
              variant="outline"
            >
              <Facebook className="size-5" />
              Xem fanpage
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-yellow-40 to-primary-50 text-white font-bold rounded-sm py-3 text-base"
              onClick={() => onSendMessage(store?.page_id ?? 0)}
              variant="default"
            >
              Chat đặt món ngay
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
